import { t } from "@lingui/macro";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import {
  AlertColor,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import Alerts from "@/client/components/alerts";
import { DateFormat } from "@/client/components/custom/common-format";
import ChipLabel from "@/client/components/custom/custom-chip";
import { useAppContext } from "@/client/hooks/context";
import { useUser } from "@/client/services/user";
import { formatStudentSchedule } from "@/client/libs/student-schedule";
import StatusConformPopup from "./status-confirm";

// ─── Styled Components (shared with Placement) ──────────────────────────────

const FilterBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.75, 2),
  backgroundColor: "#f7faff",
  borderRadius: "12px",
  border: "1px solid #dde8fb",
  marginBottom: theme.spacing(1.5),
}));

const filterSelectSx = {
  fontSize: "13px",
  bgcolor: "#fff",
  borderRadius: "8px",
  "& .MuiSelect-select": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde8fb" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2" },
};

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "12px 12px 0 0",
  border: "1px solid #eaf0fb",
  borderBottom: "none",
  boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
  maxHeight: "65vh",
  overflowY: "auto",
}));

const StyledTableHead = styled(TableHead)(() => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  "& .MuiTableCell-root": {
    backgroundColor: "#f7faff",
    fontWeight: 700,
    fontSize: "13px",
    color: "#374151",
    borderBottom: "2px solid #dde8fb",
    whiteSpace: "nowrap",
    padding: "10px 14px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "background-color 0.15s",
  "&:hover": { backgroundColor: "#f0f6ff" },
  "&:last-child .MuiTableCell-root": { borderBottom: "none" },
  "& .MuiTableCell-root": {
    fontSize: "13px",
    color: "#374151",
    borderBottom: "1px solid #eaf0fb",
    padding: "10px 14px",
  },
}));

// ─── Status Chip ─────────────────────────────────────────────────────────────

const StatusChip = ({ status }: { status: string }) => {
  switch (status) {
    case "invited":
      return (
        <ChipLabel
          color="warning"
          label={t`Invited`}
          sx={{ fontSize: "11px", p: "0px", height: "24px" }}
        />
      );
    case "accepted":
      return (
        <ChipLabel
          color="success"
          label={t`Accepted`}
          sx={{ fontSize: "11px", p: "0px", height: "24px" }}
        />
      );
    case "declined":
      return (
        <ChipLabel
          color="error"
          label={t`Declined`}
          sx={{ fontSize: "11px", p: "0px", height: "24px" }}
        />
      );
    default:
      return null;
  }
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const DISABLED_STATUSES = ["accepted", "declined"];

// ─── Main Component ──────────────────────────────────────────────────────────

const Events = () => {
  const [data, setData] = useState<
    {
      id: string;
      event_name: string;
      category: { name: string };
      start_date: string;
      start_time: string;
      start_at?: string;
      end_date: string;
      end_time: string;
      end_at?: string;
      schedule_time_zone?: string | null;
      frequency: string;
      recurrence_end_date: string | null;
      audience_status: string;
      created_at: string;
    }[]
  >([]);

  const [open, setOpen] = useState(false);
  const { user } = useUser();
  const { loading, setLoading, reloading } = useAppContext();
  const [eventId, setEventId] = useState<string>("");
  const userId = user?.studentId;

  // ─── Filters ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  // ─── Pagination ─────────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ─── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = () => {
      setLoading(true);
      const timer = setTimeout(async () => {
        try {
          const res = await axios.get(`/api/placement/events/${userId}`);
          setData(res.data.result);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }, 1000);
      return () => clearTimeout(timer);
    };
    fetchData();
  }, [reloading, userId, setLoading]);

  // ─── Reset page on filter change ────────────────────────────────────────────
  useEffect(() => {
    setPage(0);
  }, [search, filterCategory, filterStatus]);

  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({ open: false, message: "", severity: "info" });

  const handleCloseAlert = () =>
    setAlert({ open: false, message: "", severity: "info" });

  const handleStatusOpen = (id: string) => {
    setOpen(true);
    setEventId(id);
  };

  // ─── Rows — sorted latest first by start_date ───────────────────────────────
  const rows = useMemo(
    () =>
      [...data]
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        .map((item) => ({
          id: item.id,
          title: item.event_name,
          category: item.category?.name ?? "--",
          frequency: item.frequency
            ? `${item.frequency.charAt(0).toUpperCase()}${item.frequency.slice(1)}`
            : "--",
          startDate: formatStudentSchedule(item.start_at, item.start_date, item.start_time, "DD-MMM-YYYY", item.schedule_time_zone),
          startTime: formatStudentSchedule(item.start_at, item.start_date, item.start_time, "hh:mm A", item.schedule_time_zone),
          endDate:
            item.frequency &&
            item.frequency !== "once" &&
            item.recurrence_end_date
              ? DateFormat(item.recurrence_end_date)
              : formatStudentSchedule(item.end_at, item.end_date, item.end_time, "DD-MMM-YYYY", item.schedule_time_zone),
          endTime: formatStudentSchedule(item.end_at, item.end_date, item.end_time, "hh:mm A", item.schedule_time_zone),
          status: item.audience_status,
        })),
    [data]
  );

  // ─── Filter options ─────────────────────────────────────────────────────────
  const categoryOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.category)))],
    [rows]
  );

  const statusOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.status)))],
    [rows]
  );

  // ─── Filtered rows ──────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const query = search.toLowerCase().trim();
    return rows.filter((row) => {
      if (
        query &&
        !row.title.toLowerCase().includes(query) &&
        !row.category.toLowerCase().includes(query)
      )
        return false;
      if (filterCategory !== "All" && row.category !== filterCategory)
        return false;
      if (filterStatus !== "All" && row.status !== filterStatus) return false;
      return true;
    });
  }, [rows, search, filterCategory, filterStatus]);

  // ─── Paginated rows ─────────────────────────────────────────────────────────
  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const hasActiveFilters =
    search !== "" || filterCategory !== "All" || filterStatus !== "All";

  const clearFilters = () => {
    setSearch("");
    setFilterCategory("All");
    setFilterStatus("All");
  };

  return (
    <Box sx={{ flexGrow: 1, px: 1 }}>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <StatusConformPopup
        setOpen={setOpen}
        open={open}
        eventId={eventId}
        setAlert={setAlert}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            fontSize: "15px",
            fontWeight: 700,
            color: "primary.main",
            mb: "-2px",
            userSelect: "none",
          }}
        >
          {t`Events`}
        </Box>
      </Box>

      {/* Filter Bar */}
      <FilterBar sx={{ flexWrap: "nowrap", alignItems: "center", gap: 1, py: 1.25 }}>
        <FilterListIcon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: "primary.main",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {t`Filters`}
        </Typography>

        {/* Search */}
        <TextField
          size="small"
          placeholder={t`Search title, category...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { fontSize: "12px", borderRadius: "8px", bgcolor: "#fff" },
          }}
          sx={{
            flex: "1 1 140px",
            minWidth: 120,
            maxWidth: 200,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde8fb" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "none !important", outline: "none !important" },
              "& input:focus": { outline: "none !important", boxShadow: "none !important" },
          }}
        />

        {/* Category Filter */}
        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 150 }}>
          <InputLabel sx={{ fontSize: "12px" }}>{t`Category`}</InputLabel>
          <Select
            label={t`Category`}
            value={filterCategory}
            onChange={(e: SelectChangeEvent) => setFilterCategory(e.target.value)}
            sx={{
              ...filterSelectSx,
              width: "100%",
              minWidth: "unset",
              maxWidth: "unset",
              fontSize: "12px",
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
          >
            {categoryOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "12px" }}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status Filter */}
        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }}>
          <InputLabel sx={{ fontSize: "12px" }}>{t`Status`}</InputLabel>
          <Select
            label={t`Status`}
            value={filterStatus}
            onChange={(e: SelectChangeEvent) => setFilterStatus(e.target.value)}
            sx={{
              ...filterSelectSx,
              width: "100%",
              minWidth: "unset",
              maxWidth: "unset",
              fontSize: "12px",
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
          >
            {statusOptions.map((opt) => (
              <MenuItem
                key={opt}
                value={opt}
                sx={{ fontSize: "12px", textTransform: "capitalize" }}
              >
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Count + Clear */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", flexShrink: 0 }}>
          {!loading && (
            <Typography sx={{ fontSize: "12px", color: "text.disabled", whiteSpace: "nowrap" }}>
              {hasActiveFilters
                ? `${filteredRows.length} of ${rows.length} events`
                : `${rows.length} events`}
            </Typography>
          )}
          {hasActiveFilters && (
            <Chip
              label="Clear"
              size="small"
              onDelete={clearFilters}
              onClick={clearFilters}
              sx={{
                fontSize: "11px",
                bgcolor: "#fff0f0",
                color: "#c62828",
                borderColor: "#ffcdd2",
                "& .MuiChip-deleteIcon": { color: "#c62828" },
              }}
              variant="outlined"
            />
          )}
        </Box>
      </FilterBar>

      {/* Table */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2,
          }}
        >
          <CircularProgress size={36} />
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {t`Loading events...`}
          </Typography>
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {hasActiveFilters
              ? t`No events match your filters. Try adjusting or clearing them.`
              : t`No events found.`}
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            borderRadius: "12px",
            border: "1px solid #eaf0fb",
            boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <StyledTableContainer>
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 900 }}>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ width: "180px" }}>{t`Title`}</TableCell>
                  <TableCell sx={{ width: "140px" }}>{t`Category`}</TableCell>
                  <TableCell sx={{ width: "110px" }}>{t`Frequency`}</TableCell>
                  <TableCell align="center" sx={{ width: "120px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      <CalendarTodayIcon sx={{ fontSize: 13 }} />
                      {t`Start Date`}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "100px" }}>
                    {t`Start Time`}
                  </TableCell>
                  <TableCell align="center" sx={{ width: "120px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      <CalendarTodayIcon sx={{ fontSize: 13 }} />
                      {t`End Date`}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "100px" }}>
                    {t`End Time`}
                  </TableCell>
                  <TableCell align="center" sx={{ width: "100px" }}>
                    {t`Status`}
                  </TableCell>
                  <TableCell align="center" sx={{ width: "120px" }}>
                    {t`Action`}
                  </TableCell>
                </TableRow>
              </StyledTableHead>

              <TableBody>
                {paginatedRows.map((row) => (
                  <StyledTableRow key={row.id}>
                    {/* Title */}
                    <TableCell sx={{ width: "180px", overflow: "hidden" }}>
                      <Tooltip title={row.title} placement="top">
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: "primary.main",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.title}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Category */}
                    <TableCell sx={{ width: "140px", overflow: "hidden" }}>
                      <Tooltip title={row.category} placement="top">
                        <Typography
                          sx={{
                            fontSize: "13px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.category}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Frequency */}
                    <TableCell sx={{ width: "110px", overflow: "hidden" }}>
                      <Tooltip title={row.frequency} placement="top">
                        <Typography
                          sx={{
                            fontSize: "13px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.frequency}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Start Date */}
                    <TableCell align="center" sx={{ width: "120px" }}>
                      <Typography
                        sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {row.startDate}
                      </Typography>
                    </TableCell>

                    {/* Start Time */}
                    <TableCell align="center" sx={{ width: "100px" }}>
                      <Typography
                        sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {row.startTime}
                      </Typography>
                    </TableCell>

                    {/* End Date */}
                    <TableCell align="center" sx={{ width: "120px" }}>
                      <Typography
                        sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {row.endDate}
                      </Typography>
                    </TableCell>

                    {/* End Time */}
                    <TableCell align="center" sx={{ width: "100px" }}>
                      <Typography
                        sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {row.endTime}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell align="center" sx={{ width: "100px" }}>
                      <StatusChip status={row.status} />
                    </TableCell>

                    {/* Action */}
                    <TableCell align="center" sx={{ width: "120px" }}>
                      <Stack direction="row" justifyContent="center">
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          disabled={DISABLED_STATUSES.includes(row.status)}
                          onClick={() => handleStatusOpen(row.id)}
                          sx={{
                            textTransform: "none",
                            fontSize: "11px",
                            height: "26px",
                            width: "100px",
                            minWidth: "100px",
                          }}
                        >
                          {t`Confirmation`}
                        </Button>
                      </Stack>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid #eaf0fb",
              bgcolor: "#f7faff",
              fontSize: "12px",
              "& .MuiTablePagination-toolbar": { minHeight: "40px" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "12px",
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default Events;
